import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import { useGetConversationSource } from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useGetSelectedCollaborator } from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import useShouldRenderAgencyShareChat from '../../hooks/useShouldRenderAgencyShareChat';
import useShouldRenderTF911 from '../../hooks/useShouldRenderTF911';

export enum ChatComponentType {
  Conversations = 'Conversations',
  RCS = 'RCS',
  TF911 = 'TF911',
}

interface UseChatComponentsResult {
  chatComponentType: ChatComponentType | null;
  shouldRenderAgencyShareChat: boolean;
  bothChatsEnabled: boolean;
  selectedCallerID: string | null;
  isChatDisabled: boolean;
  shouldRenderCollaboratorChat: boolean;
}

export const useChatComponents = (): UseChatComponentsResult => {
  const shouldRenderAgencyShareChat = useShouldRenderAgencyShareChat();
  const collaborator = useGetSelectedCollaborator();
  const shouldRenderCollaboratorChat = Boolean(collaborator.conversationID);
  // This is for the FREE TF911 module
  const isTF911Enabled = useIsTF911Enabled();
  // This is for the PAID TF911 module

  const selectedCallerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const conversationSource = useGetConversationSource(selectedCallerID);

  // Get the rcs session if it's available
  const rcsConversation = useSelectedRcsConversation(selectedCallerID);

  // To future, revisit selectedCallerID when we support texting random number
  // This checks whether to render TF911 or not, based on whether the call is ANI/ALI with specific classes of service
  const shouldRenderTF911 = useShouldRenderTF911(selectedCallerID);

  // For styling
  const bothChatsEnabled = useMemo(() => {
    return shouldRenderAgencyShareChat && shouldRenderTF911;
  }, [shouldRenderAgencyShareChat, shouldRenderTF911]);

  const isFreeTF911Module = useMemo(
    () => isTF911Enabled && shouldRenderTF911,
    [isTF911Enabled, shouldRenderTF911],
  );

  // Determine which chat component to render
  const chatComponentType = useMemo(() => {
    if (conversationSource) {
      // All future sources should lead to here
      return ChatComponentType.Conversations;
    } else if (isFreeTF911Module) {
      if (rcsConversation && rcsConversation.data) {
        return ChatComponentType.RCS;
      } else {
        return ChatComponentType.TF911;
      }
    }
    // If there is no conversation source and the free TF911 module is enabled, render the TF911 chat component
    // Conversation has NOT started yet doesnt mean we shouldnt render the component
    if (!conversationSource && isFreeTF911Module) {
      return ChatComponentType.TF911;
    }
    return null;
  }, [isFreeTF911Module, conversationSource, rcsConversation]);

  const isChatDisabled =
    !chatComponentType &&
    !shouldRenderAgencyShareChat &&
    !shouldRenderCollaboratorChat;

  return {
    chatComponentType,
    shouldRenderAgencyShareChat,
    shouldRenderCollaboratorChat,
    bothChatsEnabled,
    selectedCallerID,
    isChatDisabled,
  };
};
